import Decimal from 'decimal.js';
import { convertUsdToEur } from './eurFx';
import type { LedgerEvent } from './types';

/** Wrapped CRO on Cronos (lowercase). Same economic unit as native CRO. */
export const WCRO_CONTRACT_LOWER = '0x5c7f8a570d578ed84e63fdfa7b1ee72deae1ae23';

/** USD-pegged stables we treat as 1 USD per token for same-tx EUR anchoring (Frankfurter only). */
const STABLE_USD_SYMBOLS = new Set(['USDC', 'USDT']);

/** Match swap proceeds / outgoing-fallback stable set (buy-side payment legs). */
const STABLE_PAYMENT_SYMBOLS = new Set([
  'USDC',
  'USDT',
  'DAI',
  'BUSD',
  'TUSD',
]);

export function needsCroAnchorPricing(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind === 'internal_transfer') return false;
  if (ev.eurValue != null) return false;
  const h = ev.cronosPriceHint;
  if (!h) return false;
  if (h.mode === 'native_cro') return true;
  if (h.mode === 'crc20' && h.contractAddress.toLowerCase() === WCRO_CONTRACT_LOWER) return true;
  return false;
}

function isUsdStableLeg(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind === 'internal_transfer') return false;
  if (ev.eurValue != null) return false;
  if (!ev.amount || !ev.amount.gt(0)) return false;
  return STABLE_USD_SYMBOLS.has(ev.asset.toUpperCase());
}

/**
 * Same-tx swap vs USDC/USDT: value the CRO leg using stable amount × USD→EUR (Frankfurter).
 */
export async function applyStablecoinAnchorsFromTxLegs(working: LedgerEvent[]): Promise<number> {
  const byRef = groupCronosByRef(working);
  let n = 0;
  for (const [, sibs] of Array.from(byRef.entries())) {
    const croUnpriced = sibs.filter(needsCroAnchorPricing);
    const stables = sibs.filter(isUsdStableLeg);
    if (croUnpriced.length !== 1 || stables.length !== 1) continue;
    const cro = croUnpriced[0];
    const st = stables[0];
    const date = new Date(cro.ts);
    try {
      const eur = await convertUsdToEur(st.amount, date);
      cro.eurValue = eur;
      cro.cronosPriceHint = undefined;
      mergeReviewFlags(cro, ['priced_from_stable_leg', 'tx_derived_price']);
      n++;
    } catch {
      /* Frankfurter failure — leave for external fallback */
    }
  }
  return n;
}

export function runSwapRatioPricingPass(working: LedgerEvent[]): number {
  const byRef = groupCronosByRef(working);
  let n = 0;
  for (const ev of working) {
    if (!isUnknownCrc20AwaitingRatio(ev)) continue;
    const sibs = byRef.get(ev.ref!.toLowerCase()) ?? [];
    const eur = tryTokenEurFromPairedCroLegs(sibs, ev);
    if (eur != null) {
      applyCroSwapRatioPrice(ev, eur);
      n++;
    }
  }
  return n;
}

/**
 * **Buy-side cost basis:** DEX swap USDC/CRO → meme token prices the **disposal** of stables/CRO first
 * (`applyCronosSwapProceedsDerivation` outgoing fallback), but the **acquisition** CRC-20 row can stay
 * unpriced — FIFO then has no EUR cost. After disposal legs carry EUR, split total paid EUR across
 * unpriced non-WCRO **acquisition** rows in the same tx (same heuristic as CRO↔token ratio: equal split).
 *
 * Run **after** `applyCronosSwapProceedsDerivation` so payment disposals are EUR-backed.
 */
export function runTokenAcquisitionFromPaidDisposalsPass(working: LedgerEvent[]): number {
  const byRef = groupCronosByRef(working);
  let n = 0;
  for (const ev of working) {
    if (!isUnpricedCrc20AcquisitionAwaitingBasis(ev)) continue;
    const sibs = byRef.get(ev.ref!.toLowerCase()) ?? [];
    const eur = tryTokenAcquisitionEurFromPairedPaymentDisposals(sibs, ev);
    if (eur != null) {
      applyTokenAcquisitionBasisFromPaidDisposals(ev, eur);
      n++;
    }
  }
  return n;
}

/** Unpriced CRC-20 acquisition (non-WCRO) — candidate for buy-side basis from payment disposals. */
export function isUnpricedCrc20AcquisitionAwaitingBasis(ev: LedgerEvent): boolean {
  if (ev.kind !== 'acquisition') return false;
  return isUnknownCrc20AwaitingRatio(ev);
}

function isPricedStableOrCroPaymentDisposal(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind !== 'disposal') return false;
  if (ev.classification === 'IGNORE_NON_TAX') return false;
  if (ev.eurValue == null || !ev.eurValue.gt(0)) return false;
  const sym = ev.asset.toUpperCase();
  if (STABLE_PAYMENT_SYMBOLS.has(sym)) return true;
  if (sym === 'CRO' || sym === 'WCRO') return true;
  if (ev.tokenContractAddress?.toLowerCase() === WCRO_CONTRACT_LOWER) return true;
  return false;
}

function totalPaidEurFromPaymentDisposals(siblings: LedgerEvent[]): Decimal | null {
  let s = new Decimal(0);
  let any = false;
  for (const ev of siblings) {
    if (!isPricedStableOrCroPaymentDisposal(ev)) continue;
    s = s.plus(ev.eurValue!);
    any = true;
  }
  return any ? s : null;
}

/**
 * Same tx: sum EUR on stable/CRO **disposals** (what left the wallet) and assign each share to
 * unpriced CRC-20 **acquisitions** (what was received).
 */
export function tryTokenAcquisitionEurFromPairedPaymentDisposals(
  siblings: LedgerEvent[],
  target: LedgerEvent
): Decimal | null {
  if (!isUnpricedCrc20AcquisitionAwaitingBasis(target)) return null;
  const waiting = siblings.filter(isUnpricedCrc20AcquisitionAwaitingBasis);
  if (waiting.length === 0 || !waiting.some((t) => t === target)) return null;
  const totalPaid = totalPaidEurFromPaymentDisposals(siblings);
  if (totalPaid == null || !totalPaid.gt(0)) return null;
  return totalPaid.div(waiting.length);
}

function applyTokenAcquisitionBasisFromPaidDisposals(ev: LedgerEvent, eurTotal: Decimal): void {
  ev.eurValue = eurTotal;
  ev.cronosPriceHint = undefined;
  mergeReviewFlags(ev, [
    'priced_from_paid_disposal_leg',
    'swap_buy_side_acquisition_basis',
  ]);
}

function mergeReviewFlags(ev: LedgerEvent, extra: string[]): void {
  const cur = ev.reviewFlags ?? [];
  const set = new Set([...cur, ...extra]);
  ev.reviewFlags = Array.from(set);
}

/** Priced CRO economic leg (native or WCRO) after anchor pass. */
export function isCroLikePriced(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.eurValue == null || !ev.eurValue.gt(0)) return false;
  if (ev.asset === 'CRO') return true;
  if (ev.asset.toUpperCase() === 'WCRO') return true;
  if (ev.tokenContractAddress?.toLowerCase() === WCRO_CONTRACT_LOWER) return true;
  return false;
}

/**
 * CRC-20 row that must not use external token APIs: still null EUR and a non-WCRO contract hint.
 */
export function isUnknownCrc20AwaitingRatio(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos') return false;
  if (ev.kind === 'internal_transfer') return false;
  if (ev.classification === 'IGNORE_NON_TAX') return false;
  if (ev.eurValue != null) return false;
  const h = ev.cronosPriceHint;
  if (!h || h.mode !== 'crc20') return false;
  if (h.contractAddress.toLowerCase() === WCRO_CONTRACT_LOWER) return false;
  if (ev.asset.toUpperCase() === 'WCRO') return false;
  return true;
}

export function groupCronosByRef(events: LedgerEvent[]): Map<string, LedgerEvent[]> {
  const m = new Map<string, LedgerEvent[]>();
  for (const ev of events) {
    if (ev.source !== 'cronos' || !ev.ref) continue;
    const k = ev.ref.toLowerCase();
    const arr = m.get(k) ?? [];
    arr.push(ev);
    m.set(k, arr);
  }
  return m;
}

/**
 * If this tx has at least one priced CRO leg and at least one unpriced CRC-20 leg, assign each
 * token leg a share of the CRO legs’ total EUR (swap value conservation). Multiple token rows in
 * the same tx **split that EUR equally** (heuristic when the DEX emits several transfers).
 */
export function tryTokenEurFromPairedCroLegs(
  siblings: LedgerEvent[],
  target: LedgerEvent
): Decimal | null {
  if (!isUnknownCrc20AwaitingRatio(target)) return null;
  const croPriced = siblings.filter(isCroLikePriced);
  const tokensWaiting = siblings.filter(isUnknownCrc20AwaitingRatio);
  if (croPriced.length === 0 || tokensWaiting.length === 0) return null;
  if (!tokensWaiting.some((t) => t === target)) return null;
  const totalCroEur = croPriced.reduce((s, e) => s.plus(e.eurValue!), new Decimal(0));
  if (!totalCroEur.gt(0)) return null;
  return totalCroEur.div(tokensWaiting.length);
}

export function applyCroSwapRatioPrice(
  ev: LedgerEvent,
  eurTotal: Decimal
): void {
  ev.eurValue = eurTotal;
  ev.cronosPriceHint = undefined;
  mergeReviewFlags(ev, ['cro_swap_ratio_priced', 'priced_from_cro_leg']);
}
